/**
 * FormatProcessor – Utility to generate and parse specialized QR code formats
 * for PasarMIKRO ecosystem.
 */

const FormatProcessor = {
  /**
   * Internal helper to calculate a 32-bit checksum for integrity verification.
   * @private
   */
  /**
   * Internal helper to calculate a 32-bit checksum for integrity verification.
   * @private
   */
  _calculateChecksum: (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i)
      hash |= 0 // Convert to 32bit integer
    }
    return (hash >>> 0).toString(16).toLowerCase().padStart(8, '0')
  },

  /**
   * Generates a formatted string for QR content based on type and payload.
   *
   * @param {'PM-Inventory' | 'createInventory' | 'searchInventory' | 'createSellTransaction' | string} type - The format type
   * @param {Object|Array} data - The payload data
   * @returns {string} The formatted QR string
   */
  generate: (type, data) => {
    // Mapping for new unified format
    const gtin = data.gtin || ''
    const serial = data.serial || data.id || data.itemId || ''
    const batch = data.batch || ''
    const expiry = data.expiry || ''

    const version = data.version || '01'
    const category = data.category || '01'
    const typeCode = data.typeCode || (type === 'PM-Inventory' ? '10' : '20')
    const rid = data.rid || ''
    const isu = data.isu || ''
    const src = data.src || ''
    const d = data.d || ''
    const l = data.l || ''
    const gln = data.gln || '9506000130001'
    const pd = data.productDescription || ''
    const action = data.action || type || ''

    // PMD handling (Base64 JSON)
    let pmdEncoded = ''
    if (
      ['createInventory', 'searchInventory', 'createSellTransaction'].includes(
        type,
      )
    ) {
      const items = Array.isArray(data.items || data)
        ? data.items || data
        : [data]
      pmdEncoded = btoa(
        JSON.stringify({
          action: type,
          ...(data.url ? { url: data.url } : {}),
          items,
        }),
      )
    } else if (data.pmd) {
      pmdEncoded =
        typeof data.pmd === 'string' ? data.pmd : btoa(JSON.stringify(data.pmd))
    } else {
      pmdEncoded = btoa(JSON.stringify({}))
    }

    // Line 1: GS1 (GTIN, SERIAL, BATCH, EXPIRY)
    const line1 = `(01)${gtin}(21)${serial}(10)${batch}(17)${expiry}`

    // Line 2: PM Metadata block
    const pmHeader = `PM:${version}:${category}:${typeCode}`
    const pmParams = `RID=${rid}|ISU=${isu}|SRC=${src}|D=${d}|L=${l}|GLN=${gln}`
    const pmCore = `|${pmHeader}|${pmParams}|`
    const checksum = FormatProcessor._calculateChecksum(pmCore)
    const line2 = `${pmCore}CHK=${checksum}|`

    // Line 3: Product Description
    const line3 = `|P=${pd}`

    // Line 4: PM Metadata Data (Base64)
    const line4 = `|PMD=${pmdEncoded}`

    return `${line1}${line2}${line3}${line4}`
  },

  /**
   * Parses a scanned QR string into a structured object.
   *
   * @param {string} scannedString - The raw string from the scanner
   * @returns {Object|null} Structured data or null if not a recognized PM format
   */
  parse: (scannedString) => {
    if (!scannedString || typeof scannedString !== 'string') return null

    // 1. Clean input: remove invisible characters (LRM, thin spaces, zero-width, etc.)
    const trimmed = scannedString
      .trim()
      // Remove invisible characters and control characters (like CR/LF) that might be in the middle of fragments
      // eslint-disable-next-line no-control-regex
      .replace(
        /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF\u200E\u200F\u2000-\u200A\u202F]/g,
        '',
      )

    // 1. New Unified Format / GS1 Extraction
    if (
      trimmed.includes('(01)') ||
      trimmed.includes('PM:') ||
      trimmed.includes('PMD=') ||
      trimmed.includes('RID=')
    ) {
      const result = { type: 'PM-Inventory' }

      // GS1 Block Extraction (Robust loop-based AI extraction)
      const gs1Regex = /\((\d{2})\)([^()|]*)/g
      let m
      while ((m = gs1Regex.exec(trimmed)) !== null) {
        const ai = m[1]
        const val = m[2] ? m[2].trim() : ''
        if (ai === '01') result.gtin = val
        if (ai === '21') result.serial = val
        if (ai === '10') result.batch = val
        if (ai === '17') result.expiry = val
      }

      // PM Metadata Extraction
      const extractField = (key, targetKey) => {
        const m = trimmed.match(
          new RegExp(`(?:^|[|()]+)${key}\\s*[=:]\\s*([^|()]+)`, 'i'),
        )
        if (m) result[targetKey || key.toLowerCase()] = m[1].trim()
      }

      extractField('RID')
      extractField('ISU')
      extractField('SRC')
      extractField('D')
      extractField('L')
      extractField('GLN')
      extractField('ACT', 'action')
      extractField('AK', 'actionKey')
      extractField('CHK', 'checksum')
      extractField('P', 'productDescription')

      const pmMatch = trimmed.match(/PM:(\d+):(\d+):(\d+)/)
      if (pmMatch) {
        result.version = pmMatch[1]
        result.category = pmMatch[2]
        result.qrType = pmMatch[3]
      }

      const pmdMatch = trimmed.match(/PMD=([^| \n()]+)/i)
      if (pmdMatch) {
        const val =
          pmdMatch[1].replace(/=+$/, '') +
          '='.repeat((4 - (pmdMatch[1].length % 4)) % 4)
        result.pmdRaw = val
        try {
          const decoded = JSON.parse(atob(val))
          result.pmd = decoded
          Object.keys(decoded).forEach((k) => {
            if (result[k] === undefined) result[k] = decoded[k]
          })
          if (decoded.action) {
            result.type = 'command'
            result.command = decoded.action
            result.items = decoded.items || []
          } else if (result.action) {
            result.type = 'command'
            result.command = result.action
            result.items = decoded.items || []
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      if (!result.command && result.action) {
        result.type = 'command'
        result.command = result.action
      }

      if (
        result.rid ||
        result.serial ||
        result.productDescription ||
        result.pmd
      )
        return result
    }

    // 2. Legacy PM-Inventory format: PM-INV|key:val|key:val...|chk:hash
    if (trimmed.startsWith('PM-INV|')) {
      const result = { type: 'PM-Inventory' }
      const parts = trimmed.split('|').slice(1)

      parts.forEach((part) => {
        const colonIndex = part.indexOf(':')
        if (colonIndex !== -1) {
          const key = part.slice(0, colonIndex)
          const value = part.slice(colonIndex + 1)

          switch (key.toUpperCase()) {
            case 'SER':
            case 'ID':
              result.serial = value
              break
            case 'BATCH':
              result.batch = value
              break
            case 'PD':
            case 'P':
              result.productDescription = value
              break
            case 'EXP':
              result.expiry = value
              break
            case 'RID':
              result.rid = value
              break
            case 'SRC':
            case 'OWN':
              result.src = value
              break
            case 'D':
              result.d = value
              break
            case 'L':
              result.l = value
              break
            case 'CHK':
              result.checksum = value
              break
            default:
              result[key] = value
          }
        }
      })
      return result
    }

    // 3. Legacy Command format: format=type|items=[...]
    if (trimmed.startsWith('format=')) {
      const body = trimmed.slice(7)
      const itemsTokenIndex = body.indexOf('items=')
      let command = null
      let items = []

      if (itemsTokenIndex !== -1) {
        command = body
          .slice(0, itemsTokenIndex)
          .replace(/[|\t\r\n\s]+$/g, '')
          .trim()
        const itemsStr = body.slice(itemsTokenIndex + 6).trim()
        try {
          items = JSON.parse(itemsStr)
        } catch (e) {
          // Ignore parsing errors
        }
      } else {
        command = body.replace(/[|\t\r\n\s]+$/g, '').trim()
      }

      return { type: 'command', command, items }
    }

    return null
  },
}

export default FormatProcessor
